import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Form, Row, Col, Spinner, Card, Badge } from "react-bootstrap";
import { FaVideo, FaCopy, FaExternalLinkAlt, FaEnvelope } from "react-icons/fa";
import axios from "axios";
import API_ROUTE from "../../utils/apiRoute";
import ZoomIntegrations from "../../components/zoom-integrations/zoom-integrations.component";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" });
};

const getDefaultDateRange = () => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - 7);
  const end = new Date(today);
  end.setDate(today.getDate() + 30);
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: fmt(start), end: fmt(end) };
};

const ZoomMeetingsPage = () => {
  const defaults = getDefaultDateRange();
  const [allMeetings, setAllMeetings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [spaceFilter, setSpaceFilter] = useState("");
  const [dateStart, setDateStart] = useState(defaults.start);
  const [dateEnd, setDateEnd] = useState(defaults.end);
  const [resendStatus, setResendStatus] = useState({});
  const [startUrlStatus, setStartUrlStatus] = useState({});

  const uniqueSpaces = useMemo(() => {
    const seen = new Map();
    allMeetings.forEach((m) => { if (!seen.has(m.spaceId)) seen.set(m.spaceId, m.spaceName); });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [allMeetings]);

  const meetings = useMemo(() =>
    spaceFilter ? allMeetings.filter((m) => String(m.spaceId) === spaceFilter) : allMeetings,
    [allMeetings, spaceFilter]
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const fetchMeetings = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateStart) params.append("dateStart", dateStart);
      if (dateEnd) params.append("dateEnd", dateEnd);
      const res = await axios.get(`${API_ROUTE}reservation/zoom-meetings?${params.toString()}`, { withCredentials: true });
      const sorted = (res.data.data.meetings || []).sort(
        (a, b) => new Date(a.timeStart) - new Date(b.timeStart)
      );
      setAllMeetings(sorted);
    } catch (err) {
      console.error("Error fetching zoom meetings:", err);
    } finally {
      setIsLoading(false);
    }
  }, [dateStart, dateEnd]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // Zoom's start_url embeds a short-lived ZAK token, so the one cached at
  // booking time is often expired by the time someone actually starts the
  // meeting. Fetch a fresh one right before opening it.
  const openStartUrl = async (reservId) => {
    setStartUrlStatus((prev) => ({ ...prev, [reservId]: "loading" }));
    try {
      const res = await axios.get(`${API_ROUTE}reservation/zoom-start-url/${reservId}`, { withCredentials: true });
      const freshUrl = res.data?.data?.startUrl;
      if (freshUrl) {
        window.open(freshUrl, "_blank", "noopener,noreferrer");
      }
      setStartUrlStatus((prev) => ({ ...prev, [reservId]: null }));
    } catch (err) {
      setStartUrlStatus((prev) => ({ ...prev, [reservId]: "error" }));
      setTimeout(() => {
        setStartUrlStatus((prev) => ({ ...prev, [reservId]: null }));
      }, 3000);
    }
  };

  const resendEmail = async (reservId) => {
    setResendStatus((prev) => ({ ...prev, [reservId]: "loading" }));
    try {
      await axios.post(`${API_ROUTE}reservation/zoom-resend-email/${reservId}`, null, { withCredentials: true });
      setResendStatus((prev) => ({ ...prev, [reservId]: "success" }));
    } catch {
      setResendStatus((prev) => ({ ...prev, [reservId]: "error" }));
    }
    setTimeout(() => {
      setResendStatus((prev) => ({ ...prev, [reservId]: null }));
    }, 3000);
  };

  return (
    <div style={{ padding: "1.5rem", maxWidth: "1200px", margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
        <FaVideo size={24} color="#2D8CFF" />
        <h3 style={{ margin: 0 }}>Sessões Zoom</h3>
      </div>

      <ZoomIntegrations />

      <div style={{ background: "#f8f9fa", borderRadius: "8px", padding: "1rem", marginBottom: "1.5rem" }}>
        <Row className="align-items-end g-2">
          <Col xs={12} md={4}>
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Espaço</Form.Label>
            <Form.Control as="select" value={spaceFilter} onChange={(e) => setSpaceFilter(e.target.value)} size="sm">
              <option value="">Todos os espaços</option>
              {uniqueSpaces.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Form.Control>
          </Col>
          <Col xs={6} md={3}>
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>De</Form.Label>
            <Form.Control type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} size="sm" />
          </Col>
          <Col xs={6} md={3}>
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Até</Form.Label>
            <Form.Control type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} size="sm" />
          </Col>
          <Col xs={12} md={2}>
            <Button size="sm" onClick={fetchMeetings} style={{ width: "100%" }}>Filtrar</Button>
          </Col>
        </Row>
      </div>

      {isLoading ? (
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <Spinner animation="border" variant="primary" />
        </div>
      ) : meetings.length === 0 ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "#6c757d" }}>
          <FaVideo size={40} style={{ marginBottom: "1rem", opacity: 0.3 }} />
          <p>Nenhuma sessão Zoom encontrada para os filtros selecionados.</p>
        </div>
      ) : (
        <Row>
          {meetings.map((meeting) => {
            const status = resendStatus[meeting._id];
            return (
              <Col xs={12} md={6} lg={4} key={meeting._id} style={{ marginBottom: "1rem" }}>
                <Card style={{ height: "100%", borderLeft: "4px solid #2D8CFF" }}>
                  <Card.Body>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>{meeting.spaceName}</div>
                        <div style={{ color: "#6c757d", fontSize: "0.82rem" }}>
                          {formatDate(meeting.timeStart)} · {formatTime(meeting.timeStart)} – {formatTime(meeting.timeEnd)}
                        </div>
                      </div>
                      <Badge variant="success" style={{ whiteSpace: "nowrap" }}>
                        <FaVideo style={{ marginRight: "3px" }} />Zoom
                      </Badge>
                    </div>

                    <div style={{ fontSize: "0.85rem", marginBottom: "0.75rem" }}>
                      <strong>Utilizador:</strong> {meeting.requester || "—"}
                    </div>

                    {meeting.zoomTitle && (
                      <div style={{ fontSize: "0.85rem", marginBottom: "0.75rem" }}>
                        <strong>Título:</strong> {meeting.zoomTitle}
                      </div>
                    )}

                    {meeting.zoomJoinUrl && (
                      <div style={{ marginBottom: "0.6rem" }}>
                        <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#0c5460", marginBottom: "2px" }}>Link convidados:</div>
                        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                          <span style={{ fontSize: "0.72rem", wordBreak: "break-all", flex: 1, color: "#495057" }}>{meeting.zoomJoinUrl}</span>
                          <Button variant="outline-secondary" size="sm" style={{ padding: "2px 6px" }} onClick={() => copyToClipboard(meeting.zoomJoinUrl)} title="Copiar"><FaCopy size={11} /></Button>
                          <Button variant="outline-info" size="sm" style={{ padding: "2px 6px" }} href={meeting.zoomJoinUrl} target="_blank" rel="noopener noreferrer" title="Abrir"><FaExternalLinkAlt size={11} /></Button>
                        </div>
                      </div>
                    )}

                    {meeting.zoomStartUrl && (
                      <div style={{ marginBottom: "0.6rem" }}>
                        <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#856404", marginBottom: "2px" }}>Link anfitrião (não partilhar):</div>
                        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                          <span style={{ fontSize: "0.72rem", wordBreak: "break-all", flex: 1, color: "#495057" }}>{meeting.zoomStartUrl}</span>
                          <Button variant="outline-secondary" size="sm" style={{ padding: "2px 6px" }} onClick={() => copyToClipboard(meeting.zoomStartUrl)} title="Copiar"><FaCopy size={11} /></Button>
                          <Button variant="outline-warning" size="sm" style={{ padding: "2px 6px" }} onClick={() => openStartUrl(meeting._id)} disabled={startUrlStatus[meeting._id] === "loading"} title="Abrir (gera link atualizado)">{startUrlStatus[meeting._id] === "loading" ? <Spinner size="sm" animation="border" /> : <FaExternalLinkAlt size={11} />}</Button>
                        </div>
                      </div>
                    )}

                    {meeting.zoomPassword && (
                      <div style={{ fontSize: "0.82rem", marginBottom: "0.75rem" }}>
                        <strong>Senha:</strong> {meeting.zoomPassword}
                      </div>
                    )}

                    <div style={{ marginTop: "0.75rem", borderTop: "1px solid #dee2e6", paddingTop: "0.75rem" }}>
                      <Button
                        size="sm"
                        variant={status === "success" ? "success" : status === "error" ? "danger" : "outline-secondary"}
                        onClick={() => resendEmail(meeting._id)}
                        disabled={status === "loading"}
                        style={{ width: "100%" }}
                      >
                        {status === "loading" ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          <><FaEnvelope style={{ marginRight: "6px" }} />{status === "success" ? "Enviado!" : status === "error" ? "Erro ao enviar" : "Reenviar email Zoom"}</>
                        )}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

export default ZoomMeetingsPage;
